import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  MusicNote as TikTokIcon,
  CameraAlt as SnapchatIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../hooks/useAuth';
import { userAPI, socialAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const Settings = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [profileData, setProfileData] = useState({
    display_name: user?.display_name || '',
    avatar_url: user?.avatar_url || ''
  });

  const { data: userProfile, isLoading } = useQuery(
    'userProfile',
    userAPI.getProfile
  );

  const { data: socialAccounts } = useQuery(
    'socialAccounts',
    socialAPI.getAccounts
  );

  const updateProfileMutation = useMutation(userAPI.updateProfile, {
    onSuccess: () => {
      queryClient.invalidateQueries('userProfile');
      setEditDialogOpen(false);
      toast.success('Profile updated successfully');
    },
    onError: () => {
      toast.error('Failed to update profile');
    }
  });

  const disconnectMutation = useMutation(
    (accountId) => socialAPI.disconnectAccount(accountId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('socialAccounts');
        setDeleteDialogOpen(false);
        toast.success('Account disconnected successfully');
      },
      onError: () => {
        toast.error('Failed to disconnect account');
      }
    }
  );

  const getPlatformIcon = (platform) => {
    const icons = {
      instagram: <InstagramIcon />,
      youtube: <YouTubeIcon />,
      facebook: <FacebookIcon />,
      tiktok: <TikTokIcon />,
      snapchat: <SnapchatIcon />
    };
    return icons[platform] || null;
  };

  const getPlatformColor = (platform) => {
    const colors = {
      instagram: '#E4405F',
      youtube: '#FF0000',
      facebook: '#1877F2',
      tiktok: '#000000',
      snapchat: '#FFFC00'
    };
    return colors[platform] || '#1976d2';
  };

  const handleEditProfile = () => {
    setProfileData({
      display_name: userProfile?.display_name || '',
      avatar_url: userProfile?.avatar_url || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveProfile = () => {
    updateProfileMutation.mutate(profileData);
  };

  const handleDisconnectAccount = (account) => {
    setSelectedAccount(account);
    setDeleteDialogOpen(true);
  };

  const confirmDisconnect = () => {
    if (selectedAccount) {
      disconnectMutation.mutate(selectedAccount.id);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading settings..." />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Profile Information
              </Typography>
              <Box display="flex" alignItems="center" mb={3}>
                <Avatar
                  src={userProfile?.avatar_url}
                  sx={{ width: 80, height: 80, mr: 3 }}
                >
                  {userProfile?.display_name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {userProfile?.display_name || 'No name set'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userProfile?.email}
                  </Typography>
                  <Chip
                    label={userProfile?.subscription_tier || 'free'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Member since {new Date(userProfile?.created_at).toLocaleDateString()}
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                startIcon={<EditIcon />}
                onClick={handleEditProfile}
                variant="outlined"
              >
                Edit Profile
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Connected Accounts */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Connected Accounts
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => window.location.href = '/'}
                  size="small"
                >
                  Connect More
                </Button>
              </Box>
              {socialAccounts?.length === 0 ? (
                <Alert severity="info">
                  No social media accounts connected yet.
                </Alert>
              ) : (
                <List>
                  {socialAccounts?.map((account) => (
                    <React.Fragment key={account.id}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: getPlatformColor(account.platform)
                            }}
                          >
                            {getPlatformIcon(account.platform)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${account.platform.charAt(0).toUpperCase() + account.platform.slice(1)}`}
                          secondary={`@${account.username}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleDisconnectAccount(account)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Account Management */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Management
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Deleting your account will permanently remove all your data and cannot be undone.
              </Alert>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
                    // Handle account deletion
                    toast.error('Account deletion not implemented yet');
                  }
                }}
              >
                Delete Account
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Display Name"
              value={profileData.display_name}
              onChange={(e) => setProfileData({ ...profileData, display_name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Avatar URL"
              value={profileData.avatar_url}
              onChange={(e) => setProfileData({ ...profileData, avatar_url: e.target.value })}
              margin="normal"
              helperText="Enter a URL to an image for your avatar"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveProfile}
            variant="contained"
            disabled={updateProfileMutation.isLoading}
          >
            {updateProfileMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Disconnect Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect your {selectedAccount?.platform} account?
            This will stop data collection from this platform.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDisconnect}
            color="error"
            variant="contained"
            disabled={disconnectMutation.isLoading}
          >
            {disconnectMutation.isLoading ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Settings;
